// Frame index over the baked atlas pages (IMPLEMENTATION §2.1 render/phaser/frames.ts): frame id → texture
// page key + frame name + source pixel size. Frames are packed trimmed to their alpha bounds (M8,
// art/atlas.ts) and registered with their untrimmed source size + trim offset, so every view places, sizes
// and crops a frame in its full source box. Every lookup that misses falls back to `item.missing`, so a
// state with content the boot catalog does not know (DEV data reload, a foreign save) still renders — and
// is counted (`missed()`: distinct ids, `__gm.render.count('missing')`; DEV warns once per id), so a frame-id
// mismatch between the renderer and the atlas catalog fails an e2e count instead of hiding in a screenshot.
import type { AtlasLayout, CanvasLike } from '../../art/atlas.ts';
import { frameId } from '../../art/catalog.ts';
import { addCanvasTexture, addFrame, type PScene } from './phaserCompat.ts';

export interface FrameRef {
  key: string;
  frame: string;
  /** Untrimmed source frame size in atlas px (crop maths). */
  w: number;
  h: number;
}

export interface FrameIndex {
  get(id: string): FrameRef;
  has(id: string): boolean;
  /** Distinct ids `get` did not know (drawn as `item.missing`) since registration. */
  missed(): number;
  /** Atlas pages registered (diagnostics). */
  readonly pages: number;
  readonly frames: number;
}

export const ATLAS_KEY_PREFIX = 'atlas';

/** Registers every baked page as a canvas texture with its frames; scratch nothing (pages are the canvases). */
export function registerAtlas(
  scene: PScene,
  layout: AtlasLayout,
  pages: readonly CanvasLike[],
): FrameIndex {
  const byId = new Map<string, FrameRef>();
  const texs = pages.map((c, i) => {
    const t = addCanvasTexture(scene, `${ATLAS_KEY_PREFIX}${i}`, c as HTMLCanvasElement);
    if (!t) throw new Error(`atlas page ${i}: addCanvas failed`);
    return t;
  });
  for (const f of layout.frames) {
    const tex = texs[f.page];
    if (!tex || !addFrame(tex, f.id, f.x, f.y, f.w, f.h, f))
      throw new Error(`atlas frame ${f.id} failed`);
    byId.set(f.id, { key: `${ATLAS_KEY_PREFIX}${f.page}`, frame: f.id, w: f.sw, h: f.sh });
  }
  const missing = byId.get(frameId.missing);
  if (!missing) throw new Error('atlas has no item.missing frame');
  const missed = new Set<string>();
  return {
    get(id) {
      const f = byId.get(id);
      if (f) return f;
      if (!missed.has(id)) {
        missed.add(id);
        if (import.meta.env.DEV)
          console.warn(`[atlas] no frame "${id}": drawn as ${frameId.missing}`);
      }
      return missing;
    },
    has: (id) => byId.has(id),
    missed: () => missed.size,
    pages: pages.length,
    frames: byId.size,
  };
}
